import { Api } from 'src/common';
import { v4 as generateUUID } from 'uuid';
import { setSessionStartTime } from './user.service';
import { AuthKey } from './auth-service';

const loggedInKey = 'smme_portal.loggedIn';
const userIdKey = 'smme_portal.userId';
const merchantIdKey = 'smme_portal.userName';

export type LoginTypeLookUpResponse = {
  requestId: string;
  requestTime: string;
  lookupStartTime: string;
  lookupEndTime: string;
  lookupDuration: number;
  user: string;
  userType: string;
  merchantFound: boolean;
  psp: string;
};

export enum merchantKey {
  merchantLookUpRespondeKey = 'loginTypeResponse',
}
export type SalesOrganization = {
  name: string;
  code: string;
};

export interface MerchantLoginResponse {
  result: {
    message: string;
    status: number;
    success: boolean;
    token: string;
    userId: string;
  };
  isBusinessError: boolean;
}
export interface OtpErrorResponse {
  type: string;
  requestId: string;
  otpAttempts: number;
  otpMaxAttempts: number;
  message: string;
}

interface Merchant {
  responseId: string;
  businessName: string;
  tradingAs: string;
  merchantNumber: string;
  psp: string;
}
export const LoginResponseMessages = {
  TIMEOUT: 'Time out',
  WRONG_CREDENTIALS: 'Wrong Credentials',
  TECHNICAL_DIFFICULTIES:
    "We're sorry, but we're experiencing technical difficulties. Please try again.",
  LOGIN_SUCCESS: 'Login successful!',
  NOT_FOUND: 'Merchant not found',
};
export interface OtpSuccessResponse {
  status: string;
  message: string;
  responseId: string;
  merchants: Merchant[];
}

export interface MerchantLoginRequestBody {
  merchantEnrollment: boolean;
  username: string;
  password: string;
  findMerchantRequestId: string;
  findMerchantResponseId: string;
}

const generateDateTime = () => {
  const now = new Date();
  return now.toISOString();
};

export async function checkLoginType(phoneNumberorMarchantID?: string) {
  const requestId = generateUUID();
  const endpoint = 'merchant/lookup';

  try {
    const { response, status } = await Api.postApiV2<LoginTypeLookUpResponse>(endpoint, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        requestId: requestId,
        requestTime: generateDateTime(),
        user: phoneNumberorMarchantID,
        userType: 'MERCHANT',
      }),
    });

    switch (status) {
      case 200:
        sessionStorage.setItem(merchantKey.merchantLookUpRespondeKey, JSON.stringify(response));
        return response;
      case 404:
        return LoginResponseMessages.NOT_FOUND;
      default:
        const errorMessage = LoginResponseMessages.TECHNICAL_DIFFICULTIES;
        return errorMessage;
    }
  } catch (error) {
    const errorMessage = LoginResponseMessages.TECHNICAL_DIFFICULTIES;
    return errorMessage;
  }
}

export type OtpResponse = OtpErrorResponse | OtpSuccessResponse;

export async function validateMerchantOpt(requestId: string, otp: string): Promise<OtpResponse> {
  const endpoint = `/merchant/validate-otp`;

  const { response, status } = await Api.patchApi<OtpResponse>(endpoint, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      requestId: requestId,
      otp: otp,
    }),
  });

  if (response && status === 200) {
    return response;
  }

  if (response && status === 400) {
    return response;
  }

  if (status > 300 && status < 500) {
    throw new Error(
      "We're sorry, but we're experiencing technical difficulties. Please try again.",
    );
  }

  throw new Error('Unexpected error occurred.');
}

export async function merchantAuth(params: MerchantLoginRequestBody) {
  const endpoint = `/login`;
  try {
    const { response, status } = await Api.post<MerchantLoginResponse>(endpoint, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    const { result } = response;

    switch (status) {
      case 200:
        sessionStorage.setItem(loggedInKey, true.toString());
        sessionStorage.setItem(userIdKey, result.userId);
        sessionStorage.setItem(merchantIdKey, params.username);
        setSessionStartTime(new Date());
        sessionStorage.setItem(AuthKey.SESSION_KEY, result.token);

        return response;
      case 408:
        return LoginResponseMessages.TIMEOUT;
      case 400:
        return LoginResponseMessages.WRONG_CREDENTIALS;
      default:
        const errorMessage = LoginResponseMessages.TECHNICAL_DIFFICULTIES;
        return errorMessage;
    }
  } catch (error) {
    const errorMessage = LoginResponseMessages.TECHNICAL_DIFFICULTIES;
    return errorMessage;
  }
}
